/** @jsxImportSource @emotion/react */
import { Typography } from '@mui/material';
import BigNumber from 'bignumber.js';
import {
  ConnectWallet,
  FormikSubmitButton,
  FormikTokenTextField,
  toast,
} from 'components';
import noop from 'noop-ts';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'translation';
import {
  convertTokensToWei,
  convertWeiToTokens,
  formatTokensToReadableValue,
  getContractAddress,
} from 'utilities';
import type { TransactionReceipt } from 'web3-core/types';

import { TOKENS } from 'constants/tokens';
import { AmountForm, ErrorCode } from 'containers/AmountForms';
import { DisableLunaUstWarningContext } from 'context/DisableLunaUstWarning';
import { VError } from 'errors/VError';
import useConvertWeiToReadableTokenString from 'hooks/useConvertWeiToReadableTokenString';
import useSuccessfulTransactionModal from 'hooks/useSuccessfulTransactionModal';

import { useStyles } from '../styles';
import TEST_IDS from './testIds';
import { GetMintedUmiOutput, useGetUmiPrice, useGetClaimableRewards } from '../../../clients/api';
import { AuthContext } from '../../../context/AuthContext';

export interface ConvertProps {
  xvsToVrtConversionRatio: BigNumber | undefined;
  userVrtBalanceWei: BigNumber | undefined;
  convertVrtLoading: boolean;
  convertVrt: (amount: string) => Promise<{ success: boolean; txHash?: string; error?: string }>;
  userMinte?: GetMintedUmiOutput;

  pumice?: string;
}

const Convert: React.FC<ConvertProps> = ({
  xvsToVrtConversionRatio,
  userVrtBalanceWei,
  convertVrtLoading,
  convertVrt,
                                             userMinte,
                                             pumice = '',
}) => {
  const styles = useStyles();
  const { account } = useContext(AuthContext);
  const accountAddress = account?.address;
  const { t, Trans } = useTranslation();
  const { openSuccessfulTransactionModal } = useSuccessfulTransactionModal();
  const userCumiBalanceWei = userMinte ? userMinte.balance : 0;

  // 获取可领取奖励
  const { data: claimableRewardsData, isLoading: isGetClaimableRewardsLoading } = useGetClaimableRewards(
    { accountAddress: accountAddress || '' },
    { enabled: !!accountAddress },
  );

  const { hasLunaOrUstCollateralEnabled, openLunaUstWarningModal } = useContext(
    DisableLunaUstWarningContext,
  );

  const readableXvsAvailable = useConvertWeiToReadableTokenString({
    valueWei: new BigNumber(userCumiBalanceWei),
    token: TOKENS.dl,
  });

  const readableUserVrtBalance = useMemo(() => {
    const userVrtBalanceTokens =
      convertWeiToTokens({
        valueWei: new BigNumber(userCumiBalanceWei),
        token: TOKENS.dl,
      });

    return formatTokensToReadableValue({
      value: new BigNumber(userVrtBalanceTokens),
      token: TOKENS.dl,
    });
  }, [userCumiBalanceWei]);

  useConvertWeiToReadableTokenString({
    valueWei: userVrtBalanceWei,
    token: TOKENS.hktlp,
  });

  const userVrtBalance = useMemo(() => {
    let totalBalance = new BigNumber(0);

    if (userMinte?.balance && new BigNumber(userMinte.balance).isGreaterThan(0)) {
      totalBalance = totalBalance.plus(new BigNumber(userMinte.balance));
    }

    if (totalBalance.isGreaterThan(0)) {
      const finalBalance = convertWeiToTokens({
        valueWei: totalBalance,
        token: TOKENS.usdt,
      }).decimalPlaces(4, BigNumber.ROUND_DOWN);
      return finalBalance.toString();
    }

    return '0';
  }, [userMinte?.balance, userMinte?.stake, userMinte?.totalStaticReward, claimableRewardsData?.claimableRewards]);

  // 计算过程展示
  const claimableAmount = claimableRewardsData?.claimableRewards || new BigNumber(0);
  const balanceAmount = userMinte?.balance ? new BigNumber(userMinte.balance) : new BigNumber(0);
  const sumWei = claimableAmount.plus(balanceAmount);
  const sumTokens = sumWei.isGreaterThan(0) ? convertWeiToTokens({ valueWei: sumWei, token: TOKENS.dl }) : new BigNumber(0);


  const onSubmit = async (vrtAmount: string) => {
    // Block action is user has LUNA or UST enabled as collateral
    if (hasLunaOrUstCollateralEnabled) {
      openLunaUstWarningModal();
      return;
    }

    try {
      const vrtAmountWei = convertTokensToWei({
        value: new BigNumber(vrtAmount),
        token: TOKENS.dl,
      });
      const result = await convertVrt(vrtAmountWei.toFixed());

      if (result.success) {
        openSuccessfulTransactionModal({
          title: t('convertVrt.successfulConvertTransactionModal.title'),
          transactionHash: result.txHash || '',
        });
      } else {
        toast.error({ message: result.error || 'Transaction failed' });
      }
    } catch (err) {
      toast.error({ message: (err as Error).message });
    }
  };

  return (
    <div css={styles.root}>
      <ConnectWallet message={t('convertVrt.connectWalletToConvertVrtToXvs')}>
        {/* 总余额显示 */}
        <section css={styles.title}>
          <Typography variant="h3" style={{ color: '#f59e0b', fontWeight: 'bold' }}>
            {userVrtBalance} USDT
          </Typography>
          <Typography variant="small2" style={{ color: '#6b7280' }}>
            可提取余额
          </Typography>
        </section>

        <AmountForm onSubmit={onSubmit} maxAmount={userVrtBalance} css={styles.form}>
          {({ values }) => (
              <>
                <div css={styles.inputSection}>
                  <Typography variant="small2" css={[styles.inputLabel, styles.fontWeight600]}>
                    {t('convertVrt.convertVrt')}
                  </Typography>
                  <FormikTokenTextField
                    token={TOKENS.usdt}
                    name="amount"
                    css={styles.input}
                    rightMaxButton={
                      userVrtBalance
                        ? {
                            label: t('convertVrt.max'),
                            valueOnClick: userVrtBalance,
                          }
                        : undefined
                    }
                    displayableErrorCodes={[ErrorCode.HIGHER_THAN_MAX]}
                    data-testid={TEST_IDS.vrtTokenTextField}
                  />
                </div>

                <FormikSubmitButton
                  css={styles.submitButton}
                  fullWidth
                  loading={convertVrtLoading}
                  enabledLabel={t('convertVrt.convertVrtToXvs')}
                />
              </>
          )}
        </AmountForm>
      </ConnectWallet>
    </div>
  );
};

export default Convert;
